import { useState, type MouseEvent } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  MoreVert,
  AssignmentAdd,
  AssignmentInd,
  DeleteOutlined,
  DriveFileRenameOutline,
  LoopOutlined,
  NotesOutlined,
} from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';

type MediaItemOverflowMenuProps = {
  mediaTitle: string;
  showRename: boolean;
  showRequest: boolean;
  showUnrequest: boolean;
  showAutoLoop: boolean;
  autoLoopEnabled: boolean;
  autoLoopDisabled?: boolean;
  showDelete: boolean;
  deletePending?: boolean;
  showNotes?: boolean;
  onEditNotes?: () => void;
  onRename: () => void;
  onToggleRequest: () => void;
  onToggleAutoLoop: () => void;
  onDelete: () => void;
};

export function MediaItemOverflowMenu({
  mediaTitle,
  showRename,
  showRequest,
  showUnrequest,
  showAutoLoop,
  autoLoopEnabled,
  autoLoopDisabled = false,
  showDelete,
  deletePending = false,
  showNotes = false,
  onEditNotes,
  onRename,
  onToggleRequest,
  onToggleAutoLoop,
  onDelete,
}: MediaItemOverflowMenuProps) {
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!showRename && !showRequest && !showUnrequest && !showAutoLoop && !showDelete && !showNotes) {
    return null;
  }

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRename = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleClose();
    onRename();
  };

  const handleToggleRequest = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleClose();
    onToggleRequest();
  };

  const handleToggleAutoLoop = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    if (autoLoopDisabled) {
      return;
    }
    handleClose();
    onToggleAutoLoop();
  };

  const handleDelete = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleClose();
    onDelete();
  };

  return (
    <>
      <Tooltip title={t('common.actions' as TranslationKey)}>
        <IconButton
          size="small"
          aria-label={t('common.actions' as TranslationKey)}
          onClick={handleOpen}
          data-testid="media-item-overflow-menu"
          sx={{
            'padding': '2px',
            '& > svg': {
              fontSize: '1.25rem',
              width: '1.25rem',
              height: '1.25rem',
            },
          }}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showNotes ? (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onEditNotes?.();
            }}
          >
            <ListItemIcon>
              <NotesOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('flows.detail.control.confidence.editNotes' as TranslationKey)} />
          </MenuItem>
        ) : null}
        {showRename ? (
          <MenuItem onClick={handleRename} data-testid="media-item-overflow-rename">
            <ListItemIcon>
              <DriveFileRenameOutline fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('media.rename' as TranslationKey)} />
          </MenuItem>
        ) : null}
        {showRequest ? (
          <MenuItem onClick={handleToggleRequest} data-testid="media-item-overflow-request">
            <ListItemIcon>
              <AssignmentAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('media.request' as TranslationKey)} secondary={mediaTitle} />
          </MenuItem>
        ) : null}
        {showUnrequest ? (
          <MenuItem onClick={handleToggleRequest} data-testid="media-item-overflow-unrequest">
            <ListItemIcon>
              <AssignmentInd fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary={t('media.confirmUnrequestTitle' as TranslationKey)} secondary={mediaTitle} />
          </MenuItem>
        ) : null}
        {showAutoLoop ? (
          <MenuItem
            onClick={handleToggleAutoLoop}
            disabled={autoLoopDisabled}
            data-testid="media-item-overflow-auto-loop"
          >
            <ListItemIcon>
              <LoopOutlined fontSize="small" color={autoLoopEnabled ? 'info' : 'disabled'} />
            </ListItemIcon>
            <ListItemText
              primary={t(
                (autoLoopEnabled
                  ? 'media.sequencedContent.autoLoopDeactivate'
                  : 'media.sequencedContent.autoLoopActivate') as TranslationKey,
              )}
            />
          </MenuItem>
        ) : null}
        {showDelete ? (
          <MenuItem onClick={handleDelete} disabled={deletePending} data-testid="media-item-overflow-delete">
            <ListItemIcon>
              <DeleteOutlined fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary={t('common.delete')} />
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
